import useFitPoem from './useFitPoem';
import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import './App.css';
import airportData from './airportData.json';
import poemData from './poemData.json';
import { mapLinks, bubblePosition } from './poemUtils';

const airports = new Map(airportData.map((airport) => [airport.code, airport]));
const numbers = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 });
const countryNames = new Intl.DisplayNames(['es'], { type: 'region' });

export default function App() {
  const [displayPoem, setDisplayPoem] = useState(true);
  const [transitionPhase, setTransitionPhase] = useState('idle');
  const [selected, setSelected] = useState(null);
  const [mapFailed, setMapFailed] = useState(false);
  const [position, setPosition] = useState({ top: 10, left: 10 });
  const anchorRef = useRef(null);
  const bubbleRef = useRef(null);
  const scrollRef = useRef(null);
  useFitPoem(scrollRef, displayPoem);
  const timeoutRef = useRef(null);

  const closeDetails = (restoreFocus = false) => {
    setSelected(null);
    if (restoreFocus) anchorRef.current?.focus();
  };

  const toggleDisplayMode = () => {
    if (transitionPhase !== 'idle') return;
    setSelected(null);
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setDisplayPoem((previous) => !previous);
      return;
    }
    setTransitionPhase('out');
    timeoutRef.current = setTimeout(() => {
      setDisplayPoem((previous) => !previous);
      setTransitionPhase('in');
      timeoutRef.current = setTimeout(() => setTransitionPhase('idle'), 260);
    }, 220);
  };

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  useEffect(() => {
    if (!selected) return;
    const escape = (event) => {
      if (event.key === 'Escape') {
        setSelected(null);
        anchorRef.current?.focus();
      }
    };
    window.addEventListener('keydown', escape);
    return () => window.removeEventListener('keydown', escape);
  }, [selected]);

  useLayoutEffect(() => {
    if (!selected) return;
    const reposition = () => {
      if (!anchorRef.current || !bubbleRef.current) return;
      const anchor = anchorRef.current.getBoundingClientRect();
      const bubble = bubbleRef.current;
      setPosition(bubblePosition(anchor, bubble.offsetWidth, bubble.offsetHeight, window.innerWidth, window.innerHeight));
    };
    reposition();
    const scroller = scrollRef.current;
    window.addEventListener('resize', reposition);
    scroller?.addEventListener('scroll', reposition);
    return () => {
      window.removeEventListener('resize', reposition);
      scroller?.removeEventListener('scroll', reposition);
    };
  }, [selected]);

  const select = (event, detail) => {
    event.stopPropagation();
    setMapFailed(false);
    anchorRef.current = event.currentTarget;
    setSelected(detail);
  };

  const backgroundClick = () => {
    if (selected) closeDetails();
    else toggleDisplayMode();
  };

  const selectedAirport = selected?.type === 'airport' ? selected.airport : null;
  const selectedLine = selected?.type === 'line' ? selected.line : null;
  const links = mapLinks(selectedAirport);

  return (
    <main className="App" onClick={backgroundClick}>
      <div className="content-container">
        <div className="content-window">
          <div className={`monospace transition-${transitionPhase}`} ref={scrollRef} aria-label={displayPoem ? 'Poema' : 'Poema en códigos IATA'}>
            {poemData.stanzas.map((stanza, stanzaIndex) => (
              <div key={stanzaIndex} className={`stanza${stanzaIndex === 0 ? ' poem-title-stanza' : ''}`}>
                {stanza.map((line) => (
                  <div className={`poem-line${stanzaIndex === 0 ? ' title-line' : ''}`} key={line.index} data-index={line.index}>
                    {displayPoem ? (
                      <button
                        type="button"
                        className={`poem-text${selectedLine?.index === line.index ? ' highlighted-line' : ''}`}
                        aria-label={`Ver itinerario: ${line.text}`}
                        aria-expanded={selectedLine?.index === line.index}
                        onClick={(event) => select(event, { type: 'line', line })}
                      >{line.text}</button>
                    ) : line.codes.map((code, index) => {
                      const key = `${line.index}-${index}`;
                      return (
                        <React.Fragment key={key}>
                          <button
                            type="button"
                            className={`code${selected?.key === key ? ' selected-code' : ''}`}
                            aria-label={`${code}: ${airports.get(code).name}`}
                            aria-expanded={selected?.key === key}
                            onClick={(event) => select(event, { type: 'airport', airport: airports.get(code), key })}
                          >{code}</button>{index < line.codes.length - 1 ? ' ' : ''}
                        </React.Fragment>
                      );
                    })}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
        <footer className="app-footer" onClick={(event) => event.stopPropagation()}>
          <span className="credits-text">Poema: Pedro Poitevin <span aria-hidden="true">|</span> Foto: Arturo Godoy</span>
          <button className="cta-button" type="button" aria-pressed={!displayPoem} onClick={toggleDisplayMode}>
            {displayPoem ? 'Ver códigos' : 'Ver poema'}
          </button>
        </footer>
      </div>
      {selected && (
        <aside
          ref={bubbleRef}
          className={`info-bubble ${selectedAirport ? 'airport-bubble' : 'line-bubble'}`}
          style={position}
          role="dialog"
          aria-label={selectedAirport ? `Aeropuerto ${selectedAirport.code}` : 'Itinerario del verso'}
          onClick={(event) => event.stopPropagation()}
        >
          <button className="close-bubble" type="button" aria-label="Cerrar información" onClick={() => closeDetails(true)}>×</button>
          {selectedAirport ? (
            <>
              <div className="airport-heading">
                <strong>{selectedAirport.name} ({selectedAirport.code})</strong>
                <div>{[selectedAirport.city, countryNames.of(selectedAirport.country)].filter(Boolean).join(', ')}</div>
              </div>
              {links && <>
                {mapFailed ? <p role="status">El mapa no está disponible en este momento.</p> :
                  <img className="airport-map" src={links.image} alt={`Mapa de ${selectedAirport.name}`} onError={() => setMapFailed(true)} />}
                
                <a className="map-link" href={links.full} target="_blank" rel="noopener noreferrer">Abrir mapa en Google Maps ↗</a>
              </>}
            </>
          ) : (
            <>
              <div className="itinerary">{selectedLine.codes.join(' ')}</div>
              <p className="length">Este recorrido mide <strong>{numbers.format(selectedLine.kilometers)} kilómetros</strong>.</p>
              <p className="carbon-footprint">Emisiones estimadas: <strong>{numbers.format(selectedLine.co2kg)} kg de CO₂ equivalente</strong>.</p>
              <p className="estimate-note">Estimación para 100 pasajeros, con un factor de forzamiento radiativo de 2.3.</p>
            </>
          )}
        </aside>
      )}
    </main>
  );
}
